export interface PresentationSlide {
  id: string;
  slideNum: string;
  tag: string;
  kicker: string;
  title: string;
  subtitle: string;
  bullets: string[];
  stats: { label: string; val: string; sub: string }[];
}

export interface DeckData {
  header: {
    stateTag: string;
    subTag: string;
    stateSubtitle: string;
    founderCockpitBtn: string;
    autoplayOnBtn: string;
    autoplayBtn: string;
    exitFullscreenBtn: string;
    fullscreenBtn: string;
    copiedBtn: string;
    shareBtn: string;
    printBtn: string;
    requestPilotBtn: string;
  };
  slideChrome: {
    slideOfTotal: string;
    useArrowsHint: string;
    showThumbnails: string;
    hideThumbnails: string;
    companySubtitle: string;
    keynoteStripTitle: string;
    keynoteStripSub: string;
  };
  catalogSection: {
    badge: string;
    title: string;
    subtitle: string;
    filterAll: string;
    filterSpeech: string;
    filterAutism: string;
    filterEarly: string;
    filterRehab: string;
    filterGov: string;
    launchDemoBtn: string;
  };
  geographySection: {
    badge: string;
    title: string;
    coverageSub: string;
    estimatedCwsn: string;
    studentsSuffix: string;
  };
  ctaSection: {
    title: string;
    subtitle: string;
    buttonText: string;
  };
  slides: PresentationSlide[];
  districts: { name: string; role: string; cwsn: string; ddrc: string }[];
  modules: {
    id: string;
    name: string;
    category: string;
    desc: string;
    tag: string;
    iconName: string;
  }[];
}

export const MP_DECK_EN: DeckData = {
  header: {
    stateTag: 'STATEWIDE CLOUD PROPOSAL',
    subTag: 'Government of Madhya Pradesh',
    stateSubtitle: 'Government of Madhya Pradesh • Samagra Shiksha & 52 DDRCs',
    founderCockpitBtn: 'Founder Cockpit',
    autoplayOnBtn: 'Autoplay On',
    autoplayBtn: 'Autoplay',
    exitFullscreenBtn: 'Exit Fullscreen',
    fullscreenBtn: 'Present Fullscreen',
    copiedBtn: 'Copied!',
    shareBtn: 'Share',
    printBtn: 'Print Deck',
    requestPilotBtn: 'Request State Pilot'
  },
  slideChrome: {
    slideOfTotal: 'Slide {curr} of {total}',
    useArrowsHint: 'Use Left/Right arrows or spacebar to navigate',
    showThumbnails: 'Show All Slides',
    hideThumbnails: 'Hide Thumbnails',
    companySubtitle: 'AbilityOS State Transformation Suite',
    keynoteStripTitle: 'Keynote Slide Navigator (10 Slides Total)',
    keynoteStripSub: 'Click thumbnail or press Left / Right arrows to navigate'
  },
  catalogSection: {
    badge: 'Complete Clinical Inventory (31 Modules)',
    title: 'All 31 Clinical Modules Integrated in AbilityOS',
    subtitle: 'Every single module is fully functioning and accessible directly through modern browsers and standalone offline installations.',
    filterAll: 'All 31 Products',
    filterSpeech: 'Speech & AAC',
    filterAutism: 'Autism & ABA',
    filterEarly: 'Early Screening',
    filterRehab: 'Physical & Neuro',
    filterGov: 'MIS & EVV',
    launchDemoBtn: 'Launch Demo'
  },
  geographySection: {
    badge: 'Statewide Geography',
    title: '52-District Rehabilitation & Special Education Deployment',
    coverageSub: '100% of MP DDRCs & CM Rise Wings',
    estimatedCwsn: 'Estimated CwSN:',
    studentsSuffix: 'Students'
  },
  ctaSection: {
    title: 'Partner with AbilityOS for Madhya Pradesh Divyangjan Empowerment',
    subtitle: 'We invite the Commissioner of Social Justice & PwD Welfare, State Project Director (Samagra Shiksha MP), and District Collectors to experience a live technical demonstration.',
    buttonText: 'Schedule Official State Demonstration'
  },
  slides: [
    {
      id: 'title-slide',
      slideNum: '01',
      tag: 'STATE INITIATIVE & MANDATE',
      kicker: 'GOVERNMENT OF MADHYA PRADESH PROPOSAL',
      title: 'Universal Special Education & Rehabilitation Cloud',
      subtitle: 'Rajya Shiksha Kendra (Samagra Shiksha MP) & Directorate of Social Justice & PwD Empowerment',
      bullets: [
        'Sovereign cloud infrastructure replacing fragmented legacy desktop systems across all 52 districts',
        'Empowering 1.5+ Lakh Children with Special Needs (CwSN) across CM Rise Schools & DDRCs',
        '100% compliant with RPwD Act (2016) 21 recognized disabilities and National Education Policy (NEP 2020)',
        'Zero vendor lock-in with open data interchange and automated UDID (Swavlamban) synchronizer'
      ],
      stats: [
        { label: 'Target Districts', val: '52', sub: 'Complete MP Coverage' },
        { label: 'Registered CwSN', val: '1.5L+', sub: 'Samagra Portal Base' },
        { label: 'Hardware Savings', val: '>85%', sub: '₹18.5 Cr Avoided CapEx' }
      ]
    },
    {
      id: '31-modules-slide',
      slideNum: '02',
      tag: 'UNIFIED CLINICAL ARCHITECTURE',
      kicker: 'FULL PRODUCT ECOSYSTEM',
      title: 'All 31 Clinical, Diagnostic & Special Ed Modules in One Unified Stack',
      subtitle: 'Eliminating 15+ Disconnected Software Licenses and Hardware Dongles',
      bullets: [
        'Speech & Language: AAC Synthesizer, Speech Articulation Formant Clinic, Core Vocab Motor AI, Indian Sign Language (ISL)',
        'Cognitive & Behavioral: Autism ABA Discrete Trial Training, VB-MAPP Assessment Protocol, Snoezelen Sensory Calm',
        'Sensory & Physical: Pure-Tone Audiometry Screener, Braille Tactile Learning, Hydrotherapy, Prosthetics Fitting',
        'State Operations & Field Care: EVV GPS Geofenced Field Visits, Student IEP Auto-Generator, Centralized School ERP'
      ],
      stats: [
        { label: 'Integrated Modules', val: '31', sub: 'Ready in 1 Platform' },
        { label: 'Disability Categories', val: '21', sub: 'RPwD 2016 Parity' },
        { label: 'Deployment Modes', val: 'Web + 4 Native', sub: 'Offline Resilient' }
      ]
    },
    {
      id: 'hardware-replacement-slide',
      slideNum: '03',
      tag: 'TCO & CAPITAL EFFICIENCY',
      kicker: 'HARDWARE-FREE REVOLUTION',
      title: '100% Commodity Hardware: Replacing ₹18.5+ Crore of Proprietary Gear in 52 DDRCs',
      subtitle: 'Dr. Speech, Pure-Tone Audiometer & Articulation Mirrors via Standard Browser WebAudio & WebSpeech',
      bullets: [
        'Dr. Speech Proprietary Box (₹4,50,000/center) replaced by real-time WebAudio formant & spectrogram visualizer',
        'Clinical Audiometer Hardware (₹3,20,000/center) replaced by calibrated Pure-Tone WebAudio Screener with standard headphones',
        'Dedicated Snoezelen Sensory Rooms (₹6,50,000/room) replaced by adaptive WebGL chromatic acoustic decompression',
        'Runs seamlessly on standard ₹20,000 Government laptops, tablets, and mobile phones with zero specialized drivers'
      ],
      stats: [
        { label: 'Legacy Cost / Center', val: '₹35.5L', sub: 'Proprietary Devices' },
        { label: 'AbilityOS Cost / Center', val: '₹3.6L', sub: 'Complete Annual Suite' },
        { label: 'Net 5-Yr State Savings', val: '₹18.5 Cr', sub: 'Direct CapEx Savings' }
      ]
    },
    {
      id: 'tribal-offline-slide',
      slideNum: '04',
      tag: 'TRIBAL & DEEP RURAL REACH',
      kicker: 'ACCESSIBLE IN ZERO-NETWORK ZONES',
      title: 'Zero-Network Resilience for Tribal Blocks (Mandla, Dindori, Jhabua, Barwani)',
      subtitle: 'Dexie.js IndexedDB Client-Side Persistence with Auto Background Reconciliation',
      bullets: [
        'Special educators and Mobile Resource Teachers (MRTs) can assess, evaluate, and provide therapy completely offline',
        'High-speed local IndexedDB caching ensures zero latency even in remote forest villages without cellular reception',
        'Automated background sync safely uploads queued clinical sessions the moment a device connects to Wi-Fi at a Block Resource Centre',
        'Natural bilingual Hindi and regional dialect (Bhili/Gondi) audio prompts designed for tribal students'
      ],
      stats: [
        { label: 'Tribal Blocks Targeted', val: '89', sub: 'Scheduled Tribal Areas' },
        { label: 'Offline Data Retention', val: '100%', sub: 'Zero Packet Loss' },
        { label: 'Vernacular Dialects', val: 'Hindi + Bhili', sub: 'Regional Speech Models' }
      ]
    },
    {
      id: 'governance-evv-slide',
      slideNum: '05',
      tag: 'GOVERNANCE & ACCOUNTABILITY',
      kicker: 'STATEWIDE TELEMETRY & AUDITING',
      title: 'Statewide 52-District MIS Dashboard & Geofenced EVV Home Visits',
      subtitle: 'Eliminating Paper Logbooks and Fraudulent Claiming with Cryptographic Verification',
      bullets: [
        'Electronic Visit Verification (EVV): GPS geofenced coordinates, photo timestamps, and parent signatures for mobile teachers',
        'Single Pane of Glass for State Mission Director, Joint Directors, and District Collectors in Bhopal',
        'Real-time tracking of total therapy hours, student developmental gains, assistive device deliveries, and clinical milestone pass rates',
        'Zero-knowledge HIPAA and FERPA encrypted audit trails ready for Comptroller & Auditor General (CAG) compliance'
      ],
      stats: [
        { label: 'Geofence Precision', val: '<15m', sub: 'GPS-Verified Check-In' },
        { label: 'Audit Trail Integrity', val: '100%', sub: 'Immutable Logging' },
        { label: 'State Dashboard Refresh', val: 'Real-Time', sub: 'Zero Lag Telemetry' }
      ]
    },
    {
      id: 'inclusive-schools-slide',
      slideNum: '06',
      tag: 'CM RISE SCHOOLS INTEGRATION',
      kicker: 'SPECIAL EDUCATION IN CLASSROOMS',
      title: 'Seamless Integration into MP CM Rise & Inclusive Schools',
      subtitle: 'Equipping General and Special Educators with Tiered Inclusive Pedagogy',
      bullets: [
        'Differentiated classroom instruction engine providing tailored accommodations for neurodivergent and sensory-impaired students',
        '1-Click automated IEP (Individualized Education Plan) generator aligned to CBSE, MP Board, and NEP 2020 frameworks',
        'Integrated Universal Design for Learning (UDL) tools: screen readers, dyslexia reading rulers, high-contrast themes, and switch scanning',
        'Collaborative Co-Teaching portal bridging regular subject teachers with visiting rehabilitation professionals'
      ],
      stats: [
        { label: 'CM Rise Target Wings', val: '350+', sub: 'Model Inclusive Labs' },
        { label: 'IEP Generation Time', val: '<3 mins', sub: 'Down from 4 Hours' },
        { label: 'UDL Accessibility', val: 'WCAG 2.2 AAA', sub: 'Full Conformance' }
      ]
    },
    {
      id: 'assistive-inventory-slide',
      slideNum: '07',
      tag: 'ALIMCO & ADIP INVENTORY',
      kicker: 'SUPPLY CHAIN & LOGISTICS',
      title: 'Complete Lifecycle Tracking of Assistive Devices Across 52 Districts',
      subtitle: 'Barcoded Wheelchair, Hearing Aid, Caliper & Braille Kit Inventory',
      bullets: [
        'Direct tracking of assistive aids distributed through ALIMCO, ADIP, and Red Cross camps across all 52 districts',
        'QR/Barcode scanning tied directly to student UDID number prevents duplicate allocations and unauthorized transfers',
        'Predictive maintenance schedules trigger notifications when hearing aids require battery replacements or calipers need sizing',
        'Statewide stock visibility ensures surplus equipment in Indore or Bhopal can be redirected to underserved rural districts'
      ],
      stats: [
        { label: 'Tracking Fidelity', val: '100%', sub: 'Unique Barcode per Aid' },
        { label: 'Duplicate Prevention', val: 'Zero', sub: 'UDID De-duplication' },
        { label: 'Maintenance Alerts', val: 'Automated', sub: '30-Day Periodic Cycles' }
      ]
    },
    {
      id: 'pilot-roadmap-slide',
      slideNum: '08',
      tag: 'IMPLEMENTATION TIMELINE',
      kicker: '30-DAY TURNKEY PILOT',
      title: 'Turnkey 30-Day Pilot Implementation Plan for Madhya Pradesh',
      subtitle: 'Rapid Proof-of-Value with Master Trainer Enablement in 4 Key Hubs',
      bullets: [
        'Week 1 (Days 1–7): Setup state master tenant, integrate Samagra Shiksha MP student data feeds, and configure 52 DDRC nodes',
        'Week 2 (Days 8–14): Deploy pilot in 4 central hubs (Bhopal, Indore, Gwalior, Jabalpur) with 50 speech therapists and special educators',
        'Week 3 (Days 15–21): Deploy offline PWA tablet suites to Mobile Resource Teachers in Mandla & Jhabua tribal blocks',
        'Week 4 (Days 22–30): Present live telemetry and executive clinical outcome report to State Project Director and District Collectors'
      ],
      stats: [
        { label: 'Pilot Duration', val: '30 Days', sub: 'Zero Upfront Risk' },
        { label: 'Master Trainers', val: '250 Staff', sub: 'Statewide Capacity' },
        { label: 'Phase 1 Hubs', val: '4 Cities', sub: 'Bhopal, Indore, Gw, Jb' }
      ]
    },
    {
      id: 'native-apps-slide',
      slideNum: '09',
      tag: 'ENTERPRISE DEPLOYMENT',
      kicker: 'CROSS-PLATFORM ARCHITECTURE',
      title: 'Four Enterprise Native Installers + Instant Browser PWA',
      subtitle: 'Verified Multi-Megabyte Binaries for State School IT & Air-Gapped Labs',
      bullets: [
        'Android Tablet APK (41.1 MB): Touch-optimized for student AAC boards and mobile teacher fieldwork',
        'Windows Desktop Suite (41.1 MB): Built for DDRC clinical therapy rooms and school computer labs',
        'macOS Executive Package (41.1 MB): Optimized for supervisory review, diagnostic reporting, and administrative planning',
        'Offline Air-Gapped Suite (41.0 MB): Self-contained zip installer for secure environments with zero external internet dependencies'
      ],
      stats: [
        { label: 'Native Binaries', val: '4 Platforms', sub: 'Android, Win, Mac, Zip' },
        { label: 'Cloud Footprint', val: 'Zero-Install', sub: 'Instant PWA in Chrome' },
        { label: 'Security Model', val: 'Air-Gapped', sub: 'No Data Leakage' }
      ]
    },
    {
      id: 'conclusion-slide',
      slideNum: '10',
      tag: 'PARTNERSHIP INVITATION',
      kicker: 'NEXT STEPS & ACTION PLAN',
      title: 'Partnering with AbilityOS for a More Inclusive Madhya Pradesh',
      subtitle: 'BugFree Solutions Pvt Ltd • Special Education & Rehabilitation Technology Division',
      bullets: [
        'Request an on-site executive presentation at Mantralaya / Rajya Shiksha Kendra, Bhopal',
        'Authorize 30-day zero-cost pilot across Bhopal & Indore DDRCs and selected CM Rise Schools',
        'Custom vernacular localization into regional tribal dialects (Bhili, Gondi, Bundelkhandi)',
        'Direct technical support line and dedicated on-ground state solutions architect assigned to MP'
      ],
      stats: [
        { label: 'State Contact', val: 'Dedicated', sub: 'MP Implementation Team' },
        { label: 'Pilot Cost', val: 'Zero Cost', sub: '30-Day Evaluation' },
        { label: 'Rollout Readiness', val: 'Immediate', sub: 'Turnkey Production Suite' }
      ]
    }
  ],
  districts: [
    { name: 'Bhopal', role: 'State Command Hub & RSK Center of Excellence', cwsn: '8,420', ddrc: 'Operational' },
    { name: 'Indore', role: 'Advanced Neuro-Rehab & Bionics Cluster', cwsn: '9,110', ddrc: 'Operational' },
    { name: 'Gwalior', role: 'Hearing & Speech Pathology Regional Hub', cwsn: '6,230', ddrc: 'Operational' },
    { name: 'Jabalpur', role: 'Orthopedic & Locomotor Rehabilitation Center', cwsn: '7,890', ddrc: 'Operational' },
    { name: 'Mandla & Dindori', role: 'Tribal Offline Tablet Deployment (Baiga Tribe Focus)', cwsn: '4,650', ddrc: 'Mobile PWA' },
    { name: 'Jhabua & Alirajpur', role: 'Bilingual Bhili/Hindi Early Screening Pilot', cwsn: '5,180', ddrc: 'Mobile PWA' }
  ],
  modules: [
    { id: 'aac', name: 'AAC Speech Synthesizer', category: 'Assistive Communication', desc: 'Fitzgerald Key color-coded symbol grid with Hindi & regional voice synthesis.', tag: 'Non-Verbal / Autism', iconName: 'Volume2' },
    { id: 'speech-therapy', name: 'Speech & Articulation Clinic', category: 'Speech Pathology', desc: 'Real-time pitch/formant visualizer replacing Dr. Speech hardware via standard mic.', tag: 'Dysarthria / Stammering', iconName: 'Activity' },
    { id: 'autism', name: 'Autism & ABA Behavioral Suite', category: 'Behavioral Health', desc: 'Discrete trial training (DTT), antecedent tracking, and positive reinforcement engines.', tag: 'ASD / ADHD', iconName: 'Brain' },
    { id: 'sign-language', name: 'Sign Language & Gesture Studio', category: 'Deaf Education', desc: 'Indian Sign Language (ISL) dictionary with 3D animated sign avatar and camera recognizer.', tag: 'Hearing Impairment', iconName: 'Eye' },
    { id: 'braille', name: 'Braille & Tactile Learning Hub', category: 'Vision Suite', desc: 'Screen-reader optimized Grade 1 & 2 Braille keyboard mapping and audio tactile lessons.', tag: 'Visual Impairment', iconName: 'Compass' },
    { id: 'student', name: 'Student IEP Progress Engine', category: 'Special Education', desc: 'Automated IDEA/NEP 2020 Individualized Education Plan builder with smart goal tracking.', tag: 'All CwSN', iconName: 'FileText' },
    { id: 'therapist', name: 'Therapist SOAP Notes & EHR', category: 'Clinical EHR', desc: 'Structured clinical documentation, ICIDH-2 coding, and multi-disciplinary case conferences.', tag: 'Clinical Staff', iconName: 'Stethoscope' },
    { id: 'teacher', name: 'Teacher Classroom Lesson Plan', category: 'Inclusive Pedagogy', desc: 'Differentiated instruction templates, sensory break alerts, and co-teaching management.', tag: 'Special Educators', iconName: 'Users' },
    { id: 'assessment', name: 'Diagnostic Clinical Assessment', category: 'Diagnostics', desc: 'Standardized psychological, cognitive, and perceptual assessment batteries with auto-scoring.', tag: 'Clinical Psych', iconName: 'Award' },
    { id: 'school-erp', name: 'Special School ERP & Timetable', category: 'Administration', desc: 'Adaptive scheduling for resource rooms, therapy slots, transport routes, and staff ratios.', tag: 'School Ops', iconName: 'Layers' },
    { id: 'hospital', name: 'Rehab Hospital Patient Inpatient', category: 'Hospital Rehab', desc: 'Bed allocation, nursing rounds, physiatry follow-ups, and discharge transition planning.', tag: 'Inpatient DDRCs', iconName: 'Building2' },
    { id: 'billing', name: 'Clinical Billing & Subsidy Manager', category: 'Finance', desc: 'Automated claim generation for ADIP, ALIMCO, Samagra Shiksha, and NDIS-equivalent schemes.', tag: 'Finance', iconName: 'TrendingUp' },
    { id: 'communication', name: 'Parent-Clinician Bridge', category: 'Family Portal', desc: 'Secure asynchronous photo/video exchange, bilingual home therapy assignments, and daily feedback.', tag: 'Family Care', iconName: 'Heart' },
    { id: 'inventory', name: 'Assistive Device & ADIP Inventory', category: 'Supply Chain', desc: 'Barcoded wheelchair, hearing aid, and caliper tracking with calibration alert cycles.', tag: 'ALIMCO / DDRC', iconName: 'RefreshCw' },
    { id: 'analytics', name: 'Executive BI & State Telemetry', category: 'Governance', desc: '52-district aggregated outcomes, therapist productivity indices, and budget burn rates.', tag: 'State Directors', iconName: 'BarChart3' },
    { id: 'ai', name: 'AI Clinical Copilot & Summarizer', category: 'AI Tools', desc: 'Automated clinical narrative summarizer, anomaly detector, and adaptive goal suggestions.', tag: 'Productivity', iconName: 'Sparkles' },
    { id: 'sensory-room', name: 'Sensory Room Snoezelen Web', category: 'Sensory Therapy', desc: 'Hardware-free acoustic relaxation, chromatic ambient cycles, and calming biofeedback.', tag: 'Sensory Overload', iconName: 'Zap' },
    { id: 'tele-rehab', name: 'Tele-Rehab WebRTC Consult', category: 'Tele-Health', desc: 'Zero-install encrypted video consulting with dual-screen interactive canvas for remote villages.', tag: 'Remote Tribal', iconName: 'Video' },
    { id: 'early-intervention', name: 'Early Intervention 0-6 Years', category: 'Pediatrics', desc: 'RBSK developmental screening, Bayley scale mapping, and infant reflex milestone logs.', tag: 'Anganwadi / RBSK', iconName: 'Activity' },
    { id: 'vocational-living', name: 'Vocational & Independent Living', category: 'Adult Rehab', desc: 'Money management, sheltered workshop task analysis, transit training, and mock store kiosks.', tag: 'Transition 14+', iconName: 'Building2' },
    { id: 'hydrotherapy', name: 'Hydrotherapy & Aquatic Protocol', category: 'Physical Rehab', desc: 'Pool temperature safety logs, buoyancy exercise regimens, and cardiac response monitors.', tag: 'Cerebral Palsy', iconName: 'Activity' },
    { id: 'music-therapy', name: 'Music Therapy & RAS Rhythm', category: 'Neuro-Rehab', desc: 'Rhythmic Auditory Stimulation (RAS) for gait synchronization and acoustic expression.', tag: 'Stroke / TBI', iconName: 'Volume2' },
    { id: 'animal-therapy', name: 'Animal-Assisted Therapy Logs', category: 'Emotional Health', desc: 'Therapy dog and equine interaction documentation with cortisol stress scale indicators.', tag: 'Emotional Needs', iconName: 'Heart' },
    { id: 'prosthetics', name: 'Prosthetics & Orthotics Fitting', category: 'Biomechanics', desc: 'Gait cycle kinematic analysis, stump circumference logs, and socket pressure mapping.', tag: 'Locomotor Disability', iconName: 'Laptop' },
    { id: 'vbmapp-assessment', name: 'VB-MAPP Behavioral Matrix', category: 'Autism Diagnostics', desc: 'Verbal Behavior Milestones Assessment and Placement Program (Milestones, Barriers, Transitions).', tag: 'ABA Clinicians', iconName: 'BarChart3' },
    { id: 'core-vocab-ai', name: 'Core Vocabulary Motor AI', category: 'AAC AI', desc: 'Predictive motor-planning layout generator tailored to high-frequency communication words.', tag: 'AAC Users', iconName: 'Sparkles' },
    { id: 'fim-weefim', name: 'FIM & WeeFIM Clinical Scoring', category: 'Functional Scoring', desc: 'Standardized Functional Independence Measure tracking self-care, sphincter control, and mobility.', tag: 'Physiotherapy', iconName: 'Award' },
    { id: 'evv-gps-tracker', name: 'EVV GPS Field Visit Tracker', category: 'Compliance', desc: 'Electronic Visit Verification with geofencing, photo check-in, and offline biometric stamps.', tag: 'Home Visits / MRTs', iconName: 'MapPin' },
    { id: 'gamified-neuro', name: 'Gamified WebGL Neuro-Rehab', category: 'Neuro-Plasticity', desc: 'Upper-limb motor games, reaction velocity challenges, and hand-eye coordination modules.', tag: 'Cognitive / Motor', iconName: 'Zap' },
    { id: 'transition-matrix', name: 'Secondary Transition Matrix', category: 'Employment', desc: 'Career interest profiler, post-school outcome goals, and job coach support matrices.', tag: 'Vocational Rehab', iconName: 'Compass' },
    { id: 'audiometry-hearing', name: 'Pure-Tone Audiometer Web', category: 'Audiology', desc: 'WebAudio pure-tone air/bone conduction screener with automated audiogram plotting.', tag: 'Hearing Screener', iconName: 'Ear' }
  ]
};

export const MP_DECK_HI: DeckData = {
  header: {
    stateTag: 'राज्यव्यापी क्लाउड प्रस्तावना',
    subTag: 'मध्य प्रदेश शासन',
    stateSubtitle: 'मध्य प्रदेश शासन • समग्र शिक्षा एवं 52 डीडीआरसी (DDRCs)',
    founderCockpitBtn: 'फाउंडर कॉकपिट',
    autoplayOnBtn: 'ऑटोप्ले चालू',
    autoplayBtn: 'ऑटोप्ले',
    exitFullscreenBtn: 'फुलस्क्रीन बंद',
    fullscreenBtn: 'फुलस्क्रीन प्रस्तुति',
    copiedBtn: 'कॉपी हुआ!',
    shareBtn: 'शेयर करें',
    printBtn: 'डेक प्रिंट करें',
    requestPilotBtn: 'राज्य पायलट का अनुरोध करें'
  },
  slideChrome: {
    slideOfTotal: 'स्लाइड {curr} / {total}',
    useArrowsHint: 'नेविगेट करने के लिए बाएं/दाएं तीर या स्पेसबार का उपयोग करें',
    showThumbnails: 'सभी स्लाइड देखें',
    hideThumbnails: 'थंबनेल छिपाएं',
    companySubtitle: 'एबिलिटीओएस (AbilityOS) राज्य रूपांतरण सुइट',
    keynoteStripTitle: 'की-नोट स्लाइड नेविगेटर (कुल 10 स्लाइड्स)',
    keynoteStripSub: 'सीधे किसी स्लाइड पर जाने के लिए थंबनेल पर क्लिक करें'
  },
  catalogSection: {
    badge: 'संपूर्ण नैदानिक उपकरण सूची (31 मॉड्यूल्स)',
    title: 'एबिलिटीओएस (AbilityOS) में एकीकृत सभी 31 क्लीनिकल व विशेष शिक्षा मॉड्यूल्स',
    subtitle: 'प्रत्येक मॉड्यूल 100% क्रियाशील है और आधुनिक ब्राउज़र तथा स्टैंडअलोन ऑफलाइन इंस्टॉलर के माध्यम से सीधे संचालित होता है।',
    filterAll: 'सभी 31 उत्पाद',
    filterSpeech: 'वाणी व एएसी (AAC)',
    filterAutism: 'ऑटिज्म एवं एबीए (ABA)',
    filterEarly: 'प्रारंभिक स्क्रीनिंग',
    filterRehab: 'शारीरिक व न्यूरो-रिहैब',
    filterGov: 'प्रशासन व ईविवी (EVV)',
    launchDemoBtn: 'डेमो खोलें'
  },
  geographySection: {
    badge: 'राज्यव्यापी भौगोलिक विस्तार',
    title: 'मध्य प्रदेश के सभी 52 जिलों में पुनर्वास एवं समावेशी शिक्षा क्रियान्वयन',
    coverageSub: '100% एमपी डीडीआरसी और सीएम राइज स्कूल्स',
    estimatedCwsn: 'अनुमानित दिव्यांग विद्यार्थी:',
    studentsSuffix: 'विशेष आवश्यकता वाले बच्चे'
  },
  ctaSection: {
    title: 'मध्य प्रदेश दिव्यांगजन सशक्तिकरण हेतु एबिलिटीओएस (AbilityOS) के साथ सहभागिता',
    subtitle: 'हम आयुक्त (सामाजिक न्याय एवं दिव्यांगजन सशक्तिकरण), राज्य परियोजना निदेशक (समग्र शिक्षा म.प्र.), तथा जिला कलेक्टर महोदयों को प्रत्यक्ष तकनीकी प्रस्तुति हेतु सादर आमंत्रित करते हैं।',
    buttonText: 'आधिकारिक राज्य प्रस्तुति शेड्यूल करें'
  },
  slides: [
    {
      id: 'title-slide',
      slideNum: '01',
      tag: 'राज्य पहल एवं जनादेश',
      kicker: 'मध्य प्रदेश शासन प्रस्तावना',
      title: 'सार्वभौमिक समावेशी शिक्षा एवं पुनर्वास क्लाउड',
      subtitle: 'राज्य शिक्षा केंद्र (समग्र शिक्षा म.प्र.) एवं सामाजिक न्याय तथा दिव्यांगजन सशक्तिकरण संचालनालय',
      bullets: [
        'सभी 52 जिलों में पुरानी और खंडित कंप्यूटर प्रणालियों को बदलकर संप्रभु क्लाउड इंफ्रास्ट्रक्चर की स्थापना',
        'सीएम राइज (CM Rise) स्कूलों और डीडीआरसी (DDRC) में 1.5 लाख से अधिक दिव्यांग बच्चों (CwSN) का सशक्तिकरण',
        'दिव्यांगजन अधिकार अधिनियम (RPwD Act 2016) की सभी 21 मान्य दिव्यांगताओं एवं राष्ट्रीय शिक्षा नीति (NEP 2020) के 100% अनुरूप',
        'ओपन डेटा इंटरचेंज एवं ऑटोमेटेड यूडीआईडी (स्वावलंबन UDID) सिंक्रोनाइजर के साथ शून्य वेंडर लॉक-इन'
      ],
      stats: [
        { label: 'लक्षित जिले', val: '52', sub: 'संपूर्ण मध्य प्रदेश कवरेज' },
        { label: 'पंजीकृत दिव्यांग छात्र', val: '1.5L+', sub: 'समग्र पोर्टल डेटाबेस' },
        { label: 'हार्डवेयर बचत', val: '>85%', sub: '₹18.5 करोड़ पूंजीगत बचत' }
      ]
    },
    {
      id: '31-modules-slide',
      slideNum: '02',
      tag: 'एकीकृत नैदानिक वास्तुकला',
      kicker: 'संपूर्ण उत्पाद ईकोसिस्टम',
      title: 'सभी 31 क्लीनिकल, डायग्नोस्टिक व विशेष शिक्षा मॉड्यूल्स एक ही प्लेटफॉर्म में',
      subtitle: '15+ अलग-अलग महंगे सॉफ्टवेयर लाइसेंस, डोंगल और हार्डवेयर की अनिवार्यता समाप्त',
      bullets: [
        'वाणी एवं भाषा: एएसी (AAC) स्पीच सिंथेसाइजर, स्पीच आर्टिकुलेशन फॉर्मेंट क्लीनिक, कोर वोकैब मोटर एआई, भारतीय सांकेतिक भाषा (ISL)',
        'संज्ञानात्मक एवं व्यवहार: ऑटिज्म एबीए ट्रायल ट्रेनिंग, वीबी-मैप (VB-MAPP) असेसमेंट प्रोटोकॉल, स्नोज़लेन संवेदी कक्ष',
        'संवेदी एवं शारीरिक: प्योर-टोन ऑडियोमेट्री हियरिंग स्क्रीनर, ब्रेल स्पर्श शिक्षण, हाइड्रोथेरेपी, प्रोस्थेटिक्स फिटिंग',
        'राज्य संचालन व फील्ड केयर: ईविवी (EVV) जीपीएस जियोफेन्स्ड गृह भेंट, छात्र आईईपी (IEP) ऑटो-जनरेटर, केंद्रीकृत स्कूल ईआरपी'
      ],
      stats: [
        { label: 'एकीकृत मॉड्यूल्स', val: '31', sub: '1 प्लेटफॉर्म में तैयार' },
        { label: 'दिव्यांगता श्रेणियां', val: '21', sub: 'RPwD 2016 शत-प्रतिशत' },
        { label: 'डिप्लॉयमेंट मोड', val: 'वेब + 4 नेटिव', sub: 'पूर्णतः ऑफलाइन सक्षम' }
      ]
    },
    {
      id: 'hardware-replacement-slide',
      slideNum: '03',
      tag: 'पूंजीगत दक्षता एवं टीसीओ (TCO)',
      kicker: 'हार्डवेयर-मुक्त क्रांति',
      title: '100% सामान्य कंप्यूटर पर संचालित: 52 डीडीआरसी में ₹18.5+ करोड़ के महंगे हार्डवेयर का प्रतिस्थापन',
      subtitle: 'डॉ. स्पीच, प्योर-टोन ऑडियोमीटर एवं आर्टिकुलेशन मिरर अब मानक ब्राउज़र वेब-ऑडियो पर उपलब्ध',
      bullets: [
        'डॉ. स्पीच का ₹4,50,000 प्रति केंद्र का महंगा बॉक्स अब सामान्य माइक्रोफोन व रियल-टाइम वेब-ऑडियो स्पेक्ट्रोग्राम से प्रतिस्थापित',
        '₹3,20,000 का क्लिनिकल ऑडियोमीटर हार्डवेयर अब साधारण हेडफ़ोन के साथ कैलिब्रेटेड प्योर-टोन स्क्रीनर से प्रतिस्थापित',
        '₹6,50,000 प्रति कक्ष का स्नोज़लेन सेंसरी रूम अब एडेप्टिव वेबजीएल (WebGL) क्रोमैटिक एकॉस्टिक डीकंप्रेशन से संचालित',
        'राज्य के सामान्य ₹20,000 वाले सरकारी लैपटॉप, टैबलेट और मोबाइल फोन पर बिना किसी अतिरिक्त ड्राइवर के तुरंत संचालित'
      ],
      stats: [
        { label: 'पारंपरिक लागत / केंद्र', val: '₹35.5L', sub: 'विशिष्ट हार्डवेयर उपकरण' },
        { label: 'AbilityOS लागत / केंद्र', val: '₹3.6L', sub: 'संपूर्ण वार्षिक सुइट' },
        { label: '5-वर्षीय कुल राज्य बचत', val: '₹18.5 Cr', sub: 'प्रत्यक्ष कैपेक्स (CapEx) बचत' }
      ]
    },
    {
      id: 'tribal-offline-slide',
      slideNum: '04',
      tag: 'जनजातीय एवं सुदूर ग्रामीण विस्तार',
      kicker: 'शून्य-इंटरनेट क्षेत्रों में भी शत-प्रतिशत सक्रिय',
      title: 'जनजातीय विकासखंडों (मंडला, डिंडोरी, झाबुआ, बड़वानी) हेतु शून्य-नेटवर्क लचीलापन',
      subtitle: 'Dexie.js IndexedDB क्लाइंट-साइड डेटा स्टोरेज एवं ऑटोमैटिक बैकग्राउंड सिंक तकनीक',
      bullets: [
        'विशेष शिक्षक और मोबाइल रिसोर्स टीचर्स (MRTs) घने वनांचल एवं नेटवर्क-विहीन क्षेत्रों में पूरी तरह ऑफलाइन असेसमेंट व थेरेपी दे सकते हैं',
        'उच्च गति लोकल डेटा स्टोरेज से बिना मोबाइल सिग्नल के भी कोई विलंब या रुकावट नहीं आती',
        'जैसे ही शिक्षक ब्लॉक रिसोर्स सेंटर (BRC) के वाई-फाई या नेटवर्क क्षेत्र में आते हैं, संपूर्ण क्लिनिकल डेटा स्वतः सुरक्षित सिंक हो जाता है',
        'जनजातीय विद्यार्थियों के लिए प्राकृतिक द्विभाषी हिंदी एवं क्षेत्रीय बोलियों (भीली, गोंडी) में ऑडियो निर्देश'
      ],
      stats: [
        { label: 'लक्षित जनजातीय ब्लॉक', val: '89', sub: 'अनुसूचित जनजातीय क्षेत्र' },
        { label: 'ऑफलाइन डेटा सुरक्षा', val: '100%', sub: 'शून्य डेटा हानि' },
        { label: 'क्षेत्रीय बोलियां', val: 'हिंदी + भीली', sub: 'स्थानीय वाणी मॉडल' }
      ]
    },
    {
      id: 'governance-evv-slide',
      slideNum: '05',
      tag: 'पारदर्शिता एवं सुशासन',
      kicker: 'राज्यव्यापी टेलीमेट्री एवं त्वरित ऑडिटिंग',
      title: 'राज्यव्यापी 52-जिला एमआईएस (MIS) डैशबोर्ड एवं जियोफेन्स्ड ईविवी (EVV) गृह भेंट प्रमाणीकरण',
      subtitle: 'कागजी रजिस्टरों और फर्जी दावों की समाप्ति हेतु क्रिप्टोग्राफिक डिजिटल सत्यापन',
      bullets: [
        'इलेक्ट्रॉनिक विजिट वेरिफिकेशन (EVV): मोबाइल शिक्षकों के लिए जीपीएस जियोफेन्स्ड कोऑर्डिनेट्स, फोटो टाइमस्टैम्प एवं अभिभावक ई-हस्ताक्षर',
        'भोपाल स्थित राज्य मिशन संचालक, संयुक्त संचालक एवं जिला कलेक्टर महोदयों हेतु सिंगल-विंडो कमांड डैशबोर्ड',
        'कुल थेरेपी घंटे, छात्र विकासात्मक प्रगति, सहायक उपकरण वितरण एवं क्लिनिकल माइलस्टोन का रियल-टाइम राज्यव्यापी विश्लेषण',
        'कैग (CAG) एवं सरकारी ऑडिट के पूर्णतः अनुकूल शून्य-ज्ञान एन्क्रिप्टेड ऑडिट ट्रेल्स'
      ],
      stats: [
        { label: 'जियोफेंस सटीकता', val: '<15m', sub: 'जीपीएस प्रमाणित चेक-इन' },
        { label: 'ऑडिट ट्रेल शुचिता', val: '100%', sub: 'अपरिवर्तनीय डिजिटल लॉग्स' },
        { label: 'डैशबोर्ड डेटा रिफ्रेश', val: 'रियल-टाइम', sub: 'शून्य विलंब टेलीमेट्री' }
      ]
    },
    {
      id: 'inclusive-schools-slide',
      slideNum: '06',
      tag: 'सीएम राइज (CM RISE) स्कूल एकीकरण',
      kicker: 'सामान्य कक्षाओं में समावेशी विशेष शिक्षा',
      title: 'मध्य प्रदेश के सीएम राइज व शासकीय समावेशी विद्यालयों में सुगम एकीकरण',
      subtitle: 'सामान्य एवं विशेष शिक्षकों को आधुनिक समावेशी शिक्षण पद्धतियों से सशक्त बनाना',
      bullets: [
        'न्यूरोडाइवर्जेंट व संवेदी-बाधित बच्चों के लिए विशेष कक्षा निर्देश व अनुकूलन मॉड्यूल',
        'सीबीएसई (CBSE), एमपी बोर्ड और राष्ट्रीय शिक्षा नीति 2020 के अनुरूप 1-क्लिक ऑटोमेटेड आईईपी (IEP) जनरेटर',
        'यूनिवर्सल डिजाइन फॉर लर्निंग (UDL) टूल्स: स्क्रीन रीडर, डिस्लेक्सिया रीडिंग रूलर, हाई-कंट्रास्ट मोड एवं स्विच स्कैनिंग',
        'सहयोगात्मक सह-शिक्षण (Co-Teaching) पोर्टल जो विषय शिक्षकों को विजिटिंग पुनर्वास विशेषज्ञों से सीधे जोड़ता है'
      ],
      stats: [
        { label: 'लक्षित सीएम राइज विंग्स', val: '350+', sub: 'मॉडल समावेशी लैब्स' },
        { label: 'IEP निर्माण समय', val: '<3 मिनट', sub: 'पहले 4 घंटे लगते थे' },
        { label: 'UDL एक्सेसिबिलिटी', val: 'WCAG 2.2 AAA', sub: 'सर्वोच्च अंतरराष्ट्रीय मानक' }
      ]
    },
    {
      id: 'assistive-inventory-slide',
      slideNum: '07',
      tag: 'एलिम्को (ALIMCO) व एडिप (ADIP) इन्वेंटरी',
      kicker: 'आपूर्ति श्रृंखला एवं उपकरण ट्रैकिंग',
      title: '52 जिलों में वितरित सहायक उपकरणों का डिजिटल जीवन-चक्र ट्रैकिंग',
      subtitle: 'बारकोडेड व्हीलचेयर, श्रवण यंत्र (Hearing Aid), कैलिपर्स एवं ब्रेल किट इन्वेंटरी',
      bullets: [
        'एलिम्को, एडिप (ADIP) और रेड क्रॉस शिविरों द्वारा वितरित सभी सहायक उपकरणों की सीधे 52 जिलों में ट्रैकिंग',
        'छात्र के यूडीआईडी (UDID) नंबर से सीधे जुड़े क्यूआर/बारकोड स्कैनिंग से उपकरणों के दोहराव या अनधिकृत हस्तांतरण पर पूर्ण रोक',
        'हियरिंग एड की बैटरी बदलने या कैलिपर्स का साइज री-एडजस्ट करने हेतु स्वचालित पूर्व-सूचना (स्मार्ट अलर्ट)',
        'राज्यव्यापी स्टॉक पारदर्शिता से इंदौर या भोपाल के अतिरिक्त उपकरण तत्काल जरूरतमंद ग्रामीण जिलों में भेजे जा सकते हैं'
      ],
      stats: [
        { label: 'ट्रैकिंग सटीकता', val: '100%', sub: 'प्रत्येक उपकरण का यूनिक कोड' },
        { label: 'डुप्लीकेशन रोकथाम', val: 'शून्य', sub: 'UDID आधारित डि-डुप्लीकेशन' },
        { label: 'रखरखाव अलर्ट', val: 'स्वचालित', sub: '30-दिवसीय आवधिक चक्र' }
      ]
    },
    {
      id: 'pilot-roadmap-slide',
      slideNum: '08',
      tag: 'क्रियान्वयन समय-सीमा',
      kicker: '30-दिवसीय टर्नकी पायलट प्रोजेक्ट',
      title: 'मध्य प्रदेश हेतु त्वरित 30-दिवसीय टर्नकी पायलट क्रियान्वयन कार्ययोजना',
      subtitle: '4 प्रमुख संभागों में मास्टर ट्रेनर प्रशिक्षण के साथ तीव्र परिणाम प्रदर्शन',
      bullets: [
        'सप्ताह 1 (दिन 1-7): राज्य मास्टर टेनेंट सेटअप, समग्र शिक्षा एमपी छात्र डेटा एकीकरण एवं 52 डीडीआरसी नोड्स का कॉन्फ़िगरेशन',
        'सप्ताह 2 (दिन 8-14): 4 केंद्रीय संभागों (भोपाल, इंदौर, ग्वालियर, जबलपुर) में 50 स्पीच थेरेपिस्ट और विशेष शिक्षकों के साथ पायलट शुरुआत',
        'सप्ताह 3 (दिन 15-21): मंडला व झाबुआ जनजातीय विकासखंडों में मोबाइल शिक्षकों को ऑफलाइन टैबलेट सुइट का वितरण',
        'सप्ताह 4 (दिन 22-30): राज्य परियोजना संचालक एवं जिला कलेक्टर महोदयों के समक्ष लाइव टेलीमेट्री एवं क्लिनिकल आउटकम रिपोर्ट प्रस्तुत करना'
      ],
      stats: [
        { label: 'पायलट अवधि', val: '30 दिन', sub: 'शून्य वित्तीय जोखिम' },
        { label: 'मास्टर ट्रेनर्स', val: '250 कर्मचारी', sub: 'राज्यव्यापी क्षमता निर्माण' },
        { label: 'प्रथम चरण केंद्र', val: '4 संभाग', sub: 'भोपाल, इंदौर, ग्वा., जब.' }
      ]
    },
    {
      id: 'native-apps-slide',
      slideNum: '09',
      tag: 'एंटरप्राइज डिप्लॉयमेंट',
      kicker: 'क्रॉस-प्लेटफॉर्म वास्तुकला',
      title: 'चार एंटरप्राइज नेटिव इंस्टॉलर्स + ब्राउज़र आधारित इंस्टेंट पीडब्ल्यूए (PWA)',
      subtitle: 'सरकारी स्कूलों की आईटी लैब्स व इंटरनेट-मुक्त केंद्रों हेतु सत्यापित मल्टी-मेगाबाइट बाइनरी',
      bullets: [
        'एंड्रॉइड टैबलेट एपीके (41.1 MB): छात्र एएसी बोर्ड एवं मोबाइल शिक्षकों की फील्ड विजिट हेतु टच-अनुकूलित',
        'विंडोज डेस्कटॉप सुइट (41.1 MB): डीडीआरसी क्लिनिकल थेरेपी रूम एवं स्कूल कंप्यूटर लैब्स हेतु विशेष रूप से निर्मित',
        'macOS सुइट (41.1 MB): पर्यवेक्षी समीक्षा, डायग्नोस्टिक रिपोर्टिंग और प्रशासनिक योजना हेतु अनुकूलित',
        'ऑफलाइन एयर-गैप्ड सुइट (41.0 MB): बिना इंटरनेट वाले अति-सुरक्षित केंद्रों हेतु स्व-निहित ज़िप इंस्टॉलर'
      ],
      stats: [
        { label: 'नेटिव बाइनरीज', val: '4 प्लेटफॉर्म्स', sub: 'Android, Win, Mac, Zip' },
        { label: 'क्लाउड इंस्टॉलेशन', val: 'शून्य इंस्टाल', sub: 'क्रोम में इंस्टेंट PWA' },
        { label: 'सुरक्षा मॉडल', val: 'एयर-गैप्ड', sub: 'शून्य डेटा रिसाव' }
      ]
    },
    {
      id: 'conclusion-slide',
      slideNum: '10',
      tag: 'साझेदारी आमंत्रण',
      kicker: 'आगामी कदम एवं कार्ययोजना',
      title: 'अधिक समावेशी एवं सशक्त मध्य प्रदेश के निर्माण हेतु एबिलिटीओएस (AbilityOS) के साथ सहभागिता',
      subtitle: 'बगफ्री सॉल्यूशंस प्राइवेट लिमिटेड • विशेष शिक्षा एवं पुनर्वास प्रौद्योगिकी प्रभाग',
      bullets: [
        'मंत्रालय / राज्य शिक्षा केंद्र (RSK), भोपाल में प्रत्यक्ष तकनीकी एवं उच्चस्तरीय प्रस्तुति का अनुरोध',
        'भोपाल व इंदौर डीडीआरसी तथा चयनित सीएम राइज विद्यालयों में 30-दिवसीय निःशुल्क पायलट को स्वीकृति',
        'क्षेत्रीय जनजातीय बोलियों (भीली, गोंडी, बुंदेलखंडी) में विशेष स्थानीय भाषा अनुकूलन',
        'मध्य प्रदेश हेतु समर्पित समाधान वास्तुकार (Solutions Architect) एवं प्रत्यक्ष तकनीकी सहायता दल की तैनाती'
      ],
      stats: [
        { label: 'राज्य संपर्क', val: 'समर्पित', sub: 'एमपी क्रियान्वयन दल' },
        { label: 'पायलट लागत', val: 'शून्य लागत', sub: '30-दिवसीय निःशुल्क मूल्यांकन' },
        { label: 'क्रियान्वयन तत्परता', val: 'तत्काल', sub: 'टर्नकी प्रोडक्शन सुइट' }
      ]
    }
  ],
  districts: [
    { name: 'भोपाल (Bhopal)', role: 'राज्य कमान केंद्र एवं राज्य शिक्षा केंद्र (RSK) सेंटर ऑफ एक्सीलेंस', cwsn: '8,420', ddrc: 'सक्रिय' },
    { name: 'इंदौर (Indore)', role: 'उन्नत न्यूरो-रिहैब एवं बायोनिक्स क्लस्टर', cwsn: '9,110', ddrc: 'सक्रिय' },
    { name: 'ग्वालियर (Gwalior)', role: 'हियरिंग एवं स्पीच पैथोलॉजी क्षेत्रीय हब', cwsn: '6,230', ddrc: 'सक्रिय' },
    { name: 'जबलपुर (Jabalpur)', role: 'ऑर्थोपेडिक व लोकोमोटर पुनर्वास केंद्र', cwsn: '7,890', ddrc: 'सक्रिय' },
    { name: 'मंडला व डिंडोरी (Mandla & Dindori)', role: 'जनजातीय ऑफलाइन टैबलेट डिप्लॉयमेंट (बैगा जनजाति विशेष फोकस)', cwsn: '4,650', ddrc: 'मोबाइल PWA' },
    { name: 'झाबुआ व अलीराजपुर (Jhabua & Alirajpur)', role: 'द्विभाषी भीली/हिंदी प्रारंभिक स्क्रीनिंग पायलट', cwsn: '5,180', ddrc: 'मोबाइल PWA' }
  ],
  modules: [
    { id: 'aac', name: 'एएसी (AAC) स्पीच सिंथेसाइजर', category: 'सहायक संप्रेषण', desc: 'हिंदी एवं क्षेत्रीय आवाज संश्लेषण युक्त फिट्ज़गेराल्ड रंग-कोडित प्रतीक ग्रिड।', tag: 'मूक-बधिर / ऑटिज्म', iconName: 'Volume2' },
    { id: 'speech-therapy', name: 'स्पीच एवं आर्टिकुलेशन क्लीनिक', category: 'वाणी पैथोलॉजी', desc: 'साधारण माइक के माध्यम से डॉ. स्पीच हार्डवेयर को प्रतिस्थापित करने वाला रियल-टाइम विजुअलाइजर।', tag: 'हकलाहट / डिस्प्रैक्सिया', iconName: 'Activity' },
    { id: 'autism', name: 'ऑटिज्म एवं एबीए (ABA) व्यवहार सुइट', category: 'व्यवहार स्वास्थ्य', desc: 'डिस्क्रीट ट्रायल ट्रेनिंग (DTT), पूर्ववृत्त ट्रैकिंग एवं सकारात्मक सुदृढ़ीकरण इंजन।', tag: 'एएसडी / एडीएचडी', iconName: 'Brain' },
    { id: 'sign-language', name: 'सांकेतिक भाषा एवं जेस्चर स्टूडियो', category: 'बधिर शिक्षा', desc: '3D एनिमेटेड साइन अवतार व कैमरा रिकग्निशन युक्त भारतीय सांकेतिक भाषा (ISL) शब्दकोश।', tag: 'श्रवण बाधित', iconName: 'Eye' },
    { id: 'braille', name: 'ब्रेल एवं स्पर्श शिक्षण केंद्र', category: 'दृष्टिबाधित सुइट', desc: 'स्क्रीन-रीडर अनुकूलित ग्रेड 1 व 2 ब्रेल कीबोर्ड मैपिंग एवं ऑडियो पाठ।', tag: 'दृष्टि बाधित', iconName: 'Compass' },
    { id: 'student', name: 'विद्यार्थी आईईपी (IEP) प्रगति इंजन', category: 'विशेष शिक्षा', desc: 'स्मार्ट लक्ष्य ट्रैकिंग युक्त स्वचालित राष्ट्रीय शिक्षा नीति 2020 व्यक्तिगत शिक्षा योजना बिल्डर।', tag: 'सभी CwSN छात्र', iconName: 'FileText' },
    { id: 'therapist', name: 'थेरेपिस्ट सोप (SOAP) नोट्स एवं ईएचआर', category: 'क्लिनिकल ईएचआर', desc: 'संरचित क्लिनिकल दस्तावेजीकरण, आईसीआईडीएच-2 कोडिंग एवं बहु-विषयक केस कॉन्फ्रेंस।', tag: 'क्लिनिकल स्टाफ', iconName: 'Stethoscope' },
    { id: 'teacher', name: 'शिक्षक कक्षा पाठ योजना', category: 'समावेशी शिक्षाशास्त्र', desc: 'विभेदित निर्देश टेम्पलेट्स, संवेदी ब्रेक अलर्ट और सह-शिक्षण प्रबंधन।', tag: 'विशेष शिक्षक', iconName: 'Users' },
    { id: 'assessment', name: 'नैदानिक मनोवैज्ञानिक मूल्यांकन', category: 'डायग्नोस्टिक्स', desc: 'स्वचालित स्कोरिंग युक्त मानकीकृत मनोवैज्ञानिक व संज्ञानात्मक मूल्यांकन बैटरी।', tag: 'क्लिनिकल साइकोलॉजी', iconName: 'Award' },
    { id: 'school-erp', name: 'विशेष विद्यालय ईआरपी एवं समय-सारणी', category: 'प्रशासन', desc: 'रिसोर्स रूम, थेरेपी स्लॉट, परिवहन मार्गों एवं स्टाफ अनुपात हेतु अनुकूली शेड्यूलिंग।', tag: 'विद्यालय संचालन', iconName: 'Layers' },
    { id: 'hospital', name: 'पुनर्वास अस्पताल रोगी इनपेशेंट', category: 'अस्पताल पुनर्वास', desc: 'बेड आवंटन, नर्सिंग राउंड, फिजियाट्री फॉलो-अप एवं डिस्चार्ज ट्रांज़िशन योजना।', tag: 'इनपेशेंट डीडीआरसी', iconName: 'Building2' },
    { id: 'billing', name: 'क्लिनिकल बिलिंग एवं अनुदान प्रबंधक', category: 'वित्त', desc: 'एडिप (ADIP), एलिम्को, समग्र शिक्षा एवं अन्य योजनाओं हेतु स्वचालित दावा निर्माण।', tag: 'अनुदान व वित्त', iconName: 'TrendingUp' },
    { id: 'communication', name: 'अभिभावक-चिकित्सक सेतु', category: 'परिवार पोर्टल', desc: 'सुरक्षित फोटो/वीडियो आदान-प्रदान, द्विभाषी गृह थेरेपी कार्य एवं दैनिक फीडबैक।', tag: 'पारिवारिक देखभाल', iconName: 'Heart' },
    { id: 'inventory', name: 'सहायक उपकरण एवं एडिप (ADIP) इन्वेंटरी', category: 'आपूर्ति श्रृंखला', desc: 'कैलिब्रेशन अलर्ट चक्र युक्त बारकोडेड व्हीलचेयर, हियरिंग एड और कैलिपर ट्रैकिंग।', tag: 'एलिम्को / डीडीआरसी', iconName: 'RefreshCw' },
    { id: 'analytics', name: 'कार्यकारी बीआई (BI) एवं राज्य टेलीमेट्री', category: 'प्रशासन व गवर्नेंस', desc: '52-जिलों के संकलित परिणाम, चिकित्सक उत्पादकता सूचकांक एवं बजट खपत दर।', tag: 'राज्य निदेशकगण', iconName: 'BarChart3' },
    { id: 'ai', name: 'एआई क्लिनिकल कोपायलट एवं सारांशकर्ता', category: 'एआई उपकरण', desc: 'स्वचालित क्लिनिकल सारांश, विसंगति पहचानकर्ता एवं अनुकूली लक्ष्य सुझाव।', tag: 'कार्यकुशलता', iconName: 'Sparkles' },
    { id: 'sensory-room', name: 'सेंसरी रूम स्नोज़लेन (Snoezelen) वेब', category: 'संवेदी थेरेपी', desc: 'हार्डवेयर-मुक्त ध्वनि विश्राम, क्रोमैटिक एम्बिएंट चक्र एवं शांत करने वाला बायोफीडबैक।', tag: 'संवेदी अधिभार', iconName: 'Zap' },
    { id: 'tele-rehab', name: 'टेली-रिहैब WebRTC वीडियो परामर्श', category: 'टेली-हेल्थ', desc: 'सुदूर गांवों हेतु दोहरी स्क्रीन वाले इंटरैक्टिव कैनवास युक्त एन्क्रिप्टेड वीडियो परामर्श।', tag: 'दूरस्थ जनजातीय क्षेत्र', iconName: 'Video' },
    { id: 'early-intervention', name: 'प्रारंभिक हस्तक्षेप 0-6 वर्ष', category: 'बाल चिकित्सा', desc: 'आरबीएसके (RBSK) विकासात्मक स्क्रीनिंग, बेले स्केल मैपिंग एवं शिशु रिफ्लेक्स माइलस्टोन लॉग्स।', tag: 'आंगनवाड़ी / आरबीएसके', iconName: 'Activity' },
    { id: 'vocational-living', name: 'व्यावसायिक एवं स्वतंत्र जीवन कौशल', category: 'वयस्क पुनर्वास', desc: 'धन प्रबंधन, शेल्टर्ड वर्कशॉप कार्य विश्लेषण, पारगमन प्रशिक्षण एवं मॉक स्टोर कियोस्क।', tag: 'संक्रमण 14+ वर्ष', iconName: 'Building2' },
    { id: 'hydrotherapy', name: 'हाइड्रोथेरेपी एवं जलीय प्रोटोकॉल', category: 'शारीरिक पुनर्वास', desc: 'पूल तापमान सुरक्षा लॉग, उत्प्लावन व्यायाम व्यवस्था एवं कार्डियक प्रतिक्रिया मॉनिटर।', tag: 'सेरेब्रल पाल्सी', iconName: 'Activity' },
    { id: 'music-therapy', name: 'संगीत चिकित्सा एवं आरएएस (RAS) रिदम', category: 'न्यूरो-रिहैब', desc: 'चाल समन्वय एवं ध्वनिक अभिव्यक्ति हेतु रिदमिक ऑडिटरी स्टिमुलेशन (RAS)।', tag: 'स्ट्रोक / टीबीआई', iconName: 'Volume2' },
    { id: 'animal-therapy', name: 'पशु-सहायता प्राप्त चिकित्सा लॉग्स', category: 'भावनात्मक स्वास्थ्य', desc: 'कॉर्टिसोल तनाव स्केल संकेतकों के साथ थेरेपी डॉग एवं हॉर्स इंटरेक्शन दस्तावेजीकरण।', tag: 'भावनात्मक आवश्यकताएं', iconName: 'Heart' },
    { id: 'prosthetics', name: 'प्रोस्थेटिक्स एवं ऑर्थोटिक्स फिटिंग', category: 'बायोमैकेनिक्स', desc: 'चाल चक्र कीनेमैटिक विश्लेषण, स्टंप परिधि लॉग एवं सॉकेट दबाव मैपिंग।', tag: 'अस्थि दिव्यांगता', iconName: 'Laptop' },
    { id: 'vbmapp-assessment', name: 'वीबी-मैप (VB-MAPP) व्यवहार मैट्रिक्स', category: 'ऑटिज्म डायग्नोस्टिक्स', desc: 'मौखिक व्यवहार मील के पत्थर मूल्यांकन और प्लेसमेंट कार्यक्रम (मील के पत्थर, बाधाएं, बदलाव)।', tag: 'एबीए चिकित्सक', iconName: 'BarChart3' },
    { id: 'core-vocab-ai', name: 'कोर वोकैबुलरी मोटर एआई', category: 'एएसी एआई', desc: 'उच्च-आवृत्ति संचार शब्दों के लिए अनुकूलित मोटर-नियोजन लेआउट जनरेटर।', tag: 'एएसी उपयोगकर्ता', iconName: 'Sparkles' },
    { id: 'fim-weefim', name: 'एफआईएम व वीएफआईएम (FIM/WeeFIM) स्कोरिंग', category: 'कार्यात्मक स्कोरिंग', desc: 'आत्म-देखभाल, स्फिंक्टर नियंत्रण एवं गतिशीलता पर नज़र रखने वाला मानकीकृत कार्यात्मक स्वतंत्रता मापदंड।', tag: 'फिजियोथेरेपी', iconName: 'Award' },
    { id: 'evv-gps-tracker', name: 'ईविवी (EVV) जीपीएस फील्ड विजिट ट्रैकर', category: 'सत्यापन व अनुपालन', desc: 'जियोफेंसिंग, फोटो चेक-इन और ऑफलाइन बायोमेट्रिक स्टैम्प के साथ इलेक्ट्रॉनिक विजिट वेरिफिकेशन।', tag: 'गृह भेंट / मोबाइल शिक्षक', iconName: 'MapPin' },
    { id: 'gamified-neuro', name: 'गेमीफाइड वेबजीएल (WebGL) न्यूरो-रिहैब', category: 'न्यूरो-प्लास्टिसिटी', desc: 'ऊपरी अंग मोटर गेम्स, प्रतिक्रिया वेग चुनौतियां एवं हाथ-आंख समन्वय मॉड्यूल।', tag: 'संज्ञानात्मक / मोटर', iconName: 'Zap' },
    { id: 'transition-matrix', name: 'माध्यमिक विद्यालय उपरांत करियर मैट्रिक्स', category: 'रोजगार व आजीविका', desc: 'करियर रुचि प्रोफाइलर, स्कूल उपरांत परिणाम लक्ष्य एवं जॉब कोच सहायता मैट्रिक्स।', tag: 'व्यावसायिक पुनर्वास', iconName: 'Compass' },
    { id: 'audiometry-hearing', name: 'प्योर-टोन ऑडियोमीटर वेब', category: 'ऑडियोलॉजी', desc: 'स्वचालित ऑडियोग्राम आलेखन के साथ वेब-ऑडियो प्योर-टोन वायु/अस्थि चालन स्क्रीनर।', tag: 'श्रवण स्क्रीनर', iconName: 'Ear' }
  ]
};
